import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Store, Star, Package, ShoppingBag, Calendar, MapPin, ArrowLeft } from 'lucide-react';
import { authAPI, productsAPI } from '../services/api';
import { getImageUrl } from '../utils/helpers';
import ProductCard from '../components/ProductCard';
import { PageTitle } from '../components/RouteTitle';

const StorePage = () => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const [storeInfo, setStoreInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadStoreData = useCallback(async () => {
    try {
      // Load store info
      const store = await authAPI.getStoreInfo(sellerId);
      setStoreInfo(store);

      // Load store products
      const storeProducts = await productsAPI.getBySeller(sellerId, false);
      setProducts(storeProducts);
    } catch (error) {
      console.error('Error loading store:', error);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => {
    loadStoreData();
  }, [loadStoreData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha no disponible';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', { year: 'numeric', month: 'long' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Store className="w-16 h-16 mx-auto text-gray-300 animate-pulse mb-4" />
          <p className="text-gray-600">Cargando tienda...</p>
        </div>
      </div>
    );
  }

  if (!storeInfo) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Card className="p-12 text-center">
          <Store className="w-24 h-24 mx-auto text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Tienda no encontrada</h2>
          <p className="text-gray-600 mb-6">
            La tienda que buscas no existe o ya no está disponible
          </p>
          <Button onClick={() => navigate('/')} className="bg-zl-orange hover:bg-zl-orange-600">
            Volver al inicio
          </Button>
        </Card>
      </div>
    );
  }

  const storeConfig = storeInfo.store_config || {};

  return (
    <div className="min-h-screen bg-gray-100">
      <PageTitle title={storeConfig.store_name || storeInfo.name || 'Tienda'} />
      {/* Store Header */}
      <div className="bg-gradient-to-r from-zl-orange to-zl-orange-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Button
            variant="ghost"
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Store Logo */}
            <div className="w-28 h-28 rounded-full bg-white p-1 shadow-lg">
              {storeConfig.store_logo ? (
                <img
                  src={getImageUrl(storeConfig.store_logo)}
                  alt={storeConfig.store_name}
                  className="w-full h-full rounded-full object-cover"
                  onError={(e) => {
                    e.target.src = storeInfo.avatar || 'https://via.placeholder.com/112x112?text=Tienda';
                  }}
                />
              ) : (
                <div className="w-full h-full rounded-full bg-zl-orange-100 flex items-center justify-center">
                  <Store className="w-12 h-12 text-zl-orange" />
                </div>
              )}
            </div>

            {/* Store Info */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2">
                {storeConfig.store_name || storeInfo.name || 'Tienda'}
              </h1>
              
              {storeConfig.address && (
                <p className="flex items-center text-zl-orange-100 mb-3">
                  <MapPin className="w-4 h-4 mr-2" />
                  {storeConfig.address}
                </p>
              )}

              {/* Store Stats */}
              <div className="flex flex-wrap gap-4 mt-4">
                <div className="flex items-center bg-white/20 rounded-lg px-4 py-2">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span className="font-semibold">{storeInfo.reputation?.toFixed(1) || '5.0'}</span>
                  <span className="ml-1 text-zl-orange-100">Reputación</span>
                </div>

                <div className="flex items-center bg-white/20 rounded-lg px-4 py-2">
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  <span className="font-semibold">{storeInfo.sales || 0}</span>
                  <span className="ml-1 text-zl-orange-100">Ventas</span>
                </div>

                <div className="flex items-center bg-white/20 rounded-lg px-4 py-2">
                  <Package className="w-5 h-5 mr-2" />
                  <span className="font-semibold">{storeInfo.products_count || 0}</span>
                  <span className="ml-1 text-zl-orange-100">Productos</span>
                </div>

                <div className="flex items-center bg-white/20 rounded-lg px-4 py-2">
                  <Calendar className="w-5 h-5 mr-2" />
                  <span className="text-zl-orange-100">Vendedor desde {formatDate(storeInfo.created_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Store Description */}
      {storeConfig.description && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-3">Sobre esta tienda</h2>
            <p className="text-gray-700">{storeConfig.description}</p>
          </Card>
        </div>
      )}

      {/* Products Section */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Productos de esta tienda</h2>
          <Badge variant="outline" className="text-lg px-4 py-1">
            {products.length} productos
          </Badge>
        </div>

        {products.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="w-24 h-24 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold mb-2">Sin productos disponibles</h3>
            <p className="text-gray-600">
              Esta tienda aún no tiene productos publicados
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map(product => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StorePage;
